"use client"

import * as React from "react"
import Image from "next/image"

import { FireIcon } from "@/components/ui/icons"
import { cn } from "@/lib/utils"
import { PICKS_MARK_PATH } from "./card-back"

/* =============================================================================
   The Picks playing card, face up.

   A playing card, not a panel. The player is the picture in the middle, the
   market is the index the way a rank is: the line big in the top-left with
   its stat under it. The crest is the suit, top-right. The name is set big
   along the bottom over the picture's fade, with the matchup under it. Same plate, shade, frame and thickness as the back.
   ============================================================================= */

export interface CardFaceData {
  name: string
  team: string
  position: string
  teamLogoUrl: string
  opponent: string
  opponentLogoUrl: string
  time: string
  imageUrl: string
  statValue: string
  statLabel: string
}

/** The example: Stafford at the Giants. ESPN CDN is on the image allow list. */
export const STAFFORD: CardFaceData = {
  name: "Matthew Stafford",
  team: "LAR",
  position: "QB",
  teamLogoUrl: "https://a.espncdn.com/i/teamlogos/nfl/500/lar.png",
  opponent: "@ NYG",
  opponentLogoUrl: "https://a.espncdn.com/i/teamlogos/nfl/500/nyg.png",
  time: "2:15am",
  imageUrl: "https://a.espncdn.com/i/headshots/nfl/players/full/12483.png",
  statValue: "32.5",
  statLabel: "Passing attempts",
}

const GROUND =
  "linear-gradient(118deg, hsl(0 0% 3%) 0%, hsl(0 0% 6%) 55%, hsl(0 0% 9%) 100%)"
const LAMP =
  "radial-gradient(130% 180% at 12% 4%, hsl(var(--primary-0) / 0.34) 0%, transparent 52%), linear-gradient(298deg, hsl(var(--black-500)) 0%, transparent 42%)"

function Index({ value, label, className }: { value: string; label: string; className?: string }) {
  return (
    <span className={cn("flex flex-col font-open-sauce leading-none", className)}>
      <span className="text-[56px] font-black tracking-[-0.04em] tabular-nums text-white">{value}</span>
      <span className="mt-1.5 text-[11px] font-semibold tracking-tight text-white/55">{label}</span>
    </span>
  )
}

export interface CardFaceProps {
  data?: CardFaceData
  width?: number
  /** The board's LIT chip under the crest. Off by default: card is card. */
  tag?: "lit" | null
  /** Load every picture at once, unoptimised: for cards that will move
      before the eye has time to wait (a drum). */
  eager?: boolean
  className?: string
}

export function CardFace({ data = STAFFORD, width = 300, tag = null, eager = false, className }: CardFaceProps) {
  const load = eager ? ({ loading: "eager", unoptimized: true } as const) : {}
  const [first, ...rest] = data.name.split(" ")
  // A picture that will not load is treated as no picture.
  const [broken, setBroken] = React.useState(false)
  React.useEffect(() => setBroken(false), [data.imageUrl])
  const last = rest.join(" ")
  return (
    <div
      className={cn(
        "relative isolate overflow-hidden rounded-[18px] ring-1 ring-inset ring-white-100",
        className
      )}
      style={{
        width,
        height: width * 1.4,
        background: GROUND,
        boxShadow:
          "inset 0 -2px 0 hsl(var(--black-0) / 0.6), inset 0 1px 0 hsl(var(--white-0) / 0.06)",
      }}
    >
      <span aria-hidden className="pointer-events-none absolute inset-0" style={{ background: LAMP }} />
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.55) 1px, transparent 1.4px)",
          backgroundSize: "7px 7px",
          opacity: 0.16,
          mixBlendMode: "overlay",
          maskImage: "linear-gradient(to right, black 0%, transparent 80%)",
          WebkitMaskImage: "linear-gradient(to right, black 0%, transparent 80%)",
        }}
      />

      {/* The mark, transparent, centred in the ground behind the player. */}
      <svg
        aria-hidden
        viewBox="0 0 40.7 31"
        className="pointer-events-none absolute left-1/2 top-[44%] w-[70%] -translate-x-1/2 -translate-y-1/2"
      >
        <path d={PICKS_MARK_PATH} fill="hsl(var(--white-0))" fillOpacity={0.07} />
      </svg>

      {/* The picture: the player standing in the lamp, his lower half falling
          into the ground so the name can sit on him. */}
      <span
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-[30%] h-[46%] w-[80%] -translate-x-1/2"
        style={{ background: "radial-gradient(closest-side, hsl(var(--primary-0) / 0.3) 0%, transparent 100%)" }}
      />
      <div className="absolute left-1/2 top-[13%] h-[60%] w-[82%] -translate-x-1/2">
        {data.imageUrl && !broken ? (
          <Image
            src={data.imageUrl}
            alt=""
            fill
            sizes="260px"
            onError={() => setBroken(true)}
            {...load}
            className="object-contain object-bottom"
            style={{
              maskImage: "linear-gradient(to bottom, black 0%, black 72%, transparent 100%)",
              WebkitMaskImage: "linear-gradient(to bottom, black 0%, black 72%, transparent 100%)",
            }}
          />
        ) : data.teamLogoUrl ? (
          /* No headshot: the crest stands in the lamp instead. */
          <div className="absolute left-1/2 top-[18%] h-[52%] w-[52%] -translate-x-1/2 opacity-90">
            <Image src={data.teamLogoUrl} alt={data.team} fill sizes="140px" {...load} className="object-contain" />
          </div>
        ) : null}
      </div>

      {/* Shade toward the edges. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(90% 80% at 50% 48%, transparent 45%, hsl(var(--black-0) / 0.28) 80%, hsl(var(--black-0) / 0.5) 100%)",
        }}
      />

      {/* The index, top-left: the line and its stat, the card's rank. */}
      <Index value={data.statValue} label={data.statLabel} className="absolute left-[9%] top-[7%]" />

      {/* The suit: the crest, top-right. */}
      <span className="absolute right-[9%] top-[7%] block size-[9%]">
        <Image src={data.teamLogoUrl} alt={data.team} fill sizes="40px" {...load} className="object-contain" />
      </span>
      {tag === "lit" && (
        <span
          className="absolute right-[9%] flex items-center gap-0.5 rounded-full bg-orange-500/15 px-1.5 py-0.5"
          style={{ top: "calc(7% + " + Math.round(width * 0.09 + 8) + "px)" }}
        >
          <FireIcon className="text-orange-400 !size-3" />
          <span className="text-[10px] font-semibold tracking-tight text-orange-400">LIT</span>
        </span>
      )}

      {/* The name along the bottom, and the matchup under it. */}
      <div className="absolute bottom-[12%] left-[9%] w-[82%] font-open-sauce">
        <p className="text-[24px] font-black leading-[0.95] tracking-[-0.03em] text-white">
          {first}
          <br />
          {last}
        </p>
        <p className="mt-2 flex items-center gap-1.5 text-[11px] tracking-tight">
          <span className="font-semibold text-grey-200">{data.team}</span>
          <span className="size-[3px] rounded-full bg-grey-500" />
          <span className="font-semibold text-grey-200">{data.position}</span>
        </p>
        <p className="mt-1 flex items-center gap-1.5 text-[11px] tracking-tight">
          <Image src={data.opponentLogoUrl} alt="" width={12} height={12} {...load} className="size-3 object-contain" />
          <span className="text-grey-300">{data.opponent}</span>
          <span className="size-[3px] rounded-full bg-grey-600" />
          <span className="text-grey-500">{data.time}</span>
        </p>
      </div>

      {/* The foot: a band under the frame with where it came from, centred. */}
      <span className="pointer-events-none absolute inset-x-0 bottom-[10px] text-center font-open-sauce text-[9px] font-semibold tracking-tight text-white/35">
        go.getpicks.app
      </span>

      {/* The frame, pressed in, same as the back, its lower edge raised to
          leave the foot band clear. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-[12px] top-[12px] bottom-[30px] rounded-[10px]"
        style={{
          boxShadow:
            "inset 0 1px 0 hsl(var(--white-0) / 0.14), inset 0 -1px 0 hsl(var(--white-0) / 0.05), 0 1px 0 hsl(var(--black-0) / 0.6)",
        }}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-[12px] top-[12px] bottom-[30px] rounded-[10px] ring-1 ring-inset ring-white-200"
      />
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-[16px] top-[16px] bottom-[34px] rounded-[7px] ring-1 ring-inset ring-white-100"
      />
    </div>
  )
}
